import * as yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js'

declare module 'yup' {
  interface StringSchema {
    noLeadingSpace(message?: string): this;
    validEmail(message?: string): this;
    validPhoneNumber(message?: string): this;
    validInternationalPhoneNumber(message?: string): this;
    validWebsite(message?: string): this;
  }

  interface NumberSchema {
    localeNumber(): this;
  }
}

/**
 * Accepts the comma decimal separator used by the de/sr locales ("16,60") on plain
 * text inputs. Without it the raw string reaches yup's number cast, fails as NaN and
 * surfaces yup's internal typeError while the user is still typing - even though the
 * same value is accepted once the field blurs and the handler normalises it.
 * An empty value becomes null so the field's own required message is shown instead.
 */
yup.addMethod(yup.number, 'localeNumber', function () {
  return this.transform((value, originalValue) => {
    if (typeof originalValue !== 'string') return value;

    const normalized = originalValue.replace(',', '.').trim();
    if (normalized === '') return null;

    const parsed = Number(normalized);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    else {
      return value;
    }
  });
});

yup.addMethod(yup.string, 'noLeadingSpace', function (message = 'Value cannot start with a space.') {
  return this.test('no-leading-space', message, (value) => {
    if (value === null || value === undefined) return true;
    return !/^\s/.test(value);
  });
});

yup.addMethod(yup.string, 'validEmail', function (message = 'Invalid email address.') {
  return this.test('valid-email', message, (value) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return value ? emailRegex.test(value) : true;
  });
});

yup.addMethod(yup.string, 'validInternationalPhoneNumber', function (message = 'Invalid international phone number.') {
  return this.test('valid-international-phone-number', message, (value) => {
    if (!value) return true;

    return isValidPhoneNumber(value);
  });
});

yup.addMethod(yup.string, 'validPhoneNumber', function (message = 'Invalid phone number.') {
  return this.test('valid-phone-number', message, (value) => {
    return value ? value.length < 20 : true;
  });
});

yup.addMethod(yup.string, 'validWebsite', function (message = 'Invalid website URL.') {
  return this.test('valid-website', message, (value) => {
    const urlRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,6}(\.[a-zA-Z]{2,6})?(\/[^\s]*)?$/;
    return value ? urlRegex.test(value) : true;
  });
});

export default yup;